import React, { useState, useEffect } from 'react';
import { FormControl } from '@material-ui/core';
import { Add } from '@material-ui/icons';
import { PasslySwitch, PageLoader, PasslyButton, PasslyTextField } from '@app/components';
import { MainRule } from './components';
import { IPolicyDefaults, Conjuction, IGlobalState, IAdditionalRule } from '@app/models';
import { useSelector, useDispatch } from 'react-redux';
import { addRule, resetRules } from '@app/store/actions';
import { policiesAuthService } from '@app/services';
import { AxiosResponse } from 'axios';
import { useTypedTranslation } from '@app/hooks';
import { genericErrorToast } from '@app/helpers';

interface IProps {
    handleValidation: Function;
    isDefault: Function;
    policyName: Function;
    isEdit: boolean;
    data?: {
        name: string;
        default: boolean;
    };
    rulesLoaded?: boolean;
}

export const PolicyForm = (props: IProps) => {
    const { handleValidation, isDefault, policyName, isEdit, rulesLoaded, data } = props;
    const mainRule = useSelector((state: IGlobalState) => state.policiesAuth.mainRule);
    const { translation } = useTypedTranslation();
    const dispatch = useDispatch();

    const [defaultPolicy, setDefaultPolicy] = useState<boolean>(data?.default ?? false);
    const [name, setName] = useState<string>(data?.name ?? '');
    const [isFormValid, setIsFormValid] = useState<{ [key: string]: boolean }>({});
    const [policyDefaults, setPolicyDefaults] = useState<IPolicyDefaults | null>(null);
    const actionsAndConditionsLoaded = (isEdit && mainRule?.conditions?.length && mainRule?.actions?.length) || (!isEdit && mainRule?.conditions && mainRule?.actions);

    const emptyAdditionalRule: IAdditionalRule = {
        operator: Conjuction.and,
        conditions: [],
        actions: []
    };

    useEffect(() => {
        policiesAuthService.getAuthPolicyDefaults(genericErrorToast, handleSuccess);

        return () => {
            dispatch(resetRules());
        };
    }, []);

    /*    useEffect(() => {
        switch (isEdit) {
            case false:
                if (rules.length === 0) dispatch(addRule(emptyRule));
                break;
            case true:
                if (rulesLoaded && rules.length === 0) dispatch(addRule(emptyRule));
                break;
        }
    }, [rules, isEdit, rulesLoaded]);*/

    useEffect(() => {
        let isValidForm = false;
        let validRulesCount = 0;
        const rulesKeys = Object.keys(isFormValid);

        rulesKeys.forEach((ruleId) => {
            if (isFormValid[ruleId]) {
                validRulesCount++;
            }
        });

        if (mainRule.elseRules?.length + 1 === validRulesCount) {
            isValidForm = true;
        }

        if (name !== '' && isValidForm) {
            handleValidation(true);
            policyName(name);
        } else {
            handleValidation(false);
            policyName(data?.name ?? '');
        }
    }, [name, isFormValid, data]);

    useEffect(() => {
        isDefault(defaultPolicy);
    }, [defaultPolicy]);

    useEffect(() => {
        if (data !== undefined) {
            setName(data.name);
            setDefaultPolicy(data.default);
        }
    }, [data]);

    const handleSuccess = (response: AxiosResponse) => {
        const defaults: IPolicyDefaults = response.data.Authentication;
        setPolicyDefaults(defaults);
    };

    const handleNameChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        const value = event.target.value as string;
        setName(value);
    };

    const validationCallback = (valid: boolean, ruleId: string, removeRule?: boolean) => {
        setIsFormValid((prevIsFormValid) => {
            let result;

            if (removeRule) {
                result = { ...prevIsFormValid };
                delete result[ruleId];
            } else {
                result = { ...prevIsFormValid, [ruleId]: valid };
            }

            return result;
        });
    };

    const toggleAsDefaultPolicy = () => setDefaultPolicy(!defaultPolicy);

    return (
        <div className={isEdit ? 'policyForm policyForm--edit' : 'policyForm policyForm--add'}>
            <div className='policyForm__header'>
                <FormControl>
                    <PasslyTextField
                        id={translation('POLICY_NAME')}
                        label={'POLICY_NAME'}
                        name={translation('POLICY_NAME')}
                        value={name}
                        onChange={handleNameChange}
                        placeholder={translation('EXAMPLE_WINDOWS_POLICY')}
                        required
                        autoFocus
                    />
                </FormControl>
                <div className='policyForm__switch-block'>
                    <PasslySwitch
                        className='policyForm__switch'
                        checked={defaultPolicy}
                        onChange={toggleAsDefaultPolicy}
                        label={translation('AUTH_POLICY_SET_DEFAULT')}
                    />
                    <PasslyButton variant='contained' color='primary' startIcon={<Add />} onClick={() => dispatch(addRule(emptyAdditionalRule))}>
                        {translation('POLICY_ADD_RULE')}
                    </PasslyButton>
                </div>
            </div>
            <div className='policyForm__rules'>
                {isEdit && !rulesLoaded && <PageLoader />}
                {policyDefaults !== null && actionsAndConditionsLoaded ? (
                    <MainRule mainRule={mainRule} defaults={policyDefaults} handleValidation={validationCallback} isEdit={isEdit} rulesLoaded={rulesLoaded} />
                ) : null}
            </div>
        </div>
    );
};
